/**
 * «Come si riconosce sullo scaffale» — la sezione della scheda componente nata con l'ondata
 * zero del magazzino (capitolo 162): marchio del registro, codice del costruttore scritto in
 * un modo solo, codici a barre, unità con cui si conta, confezioni, matricola per pezzo.
 *
 * È un pezzo di form CONTROLLATO: tiene i valori in una forma comoda per l'editing e li
 * traduce in un patch per il servizio solo al salvataggio (`aPatch`). Nessuna scrittura qui.
 *
 * Scelte visive, tutte al servizio del significato:
 *   · il codice normalizzato si vede ma non si batte: è DERIVATO dal codice del costruttore,
 *     e mostrarlo vivo mentre si scrive spiega da solo cosa fa la normalizzazione;
 *   · le matricole sono un controllo a QUATTRO stati e non un interruttore: «come la categoria» è il caso
 *     normale, e l'eccezione può dire obbligatorie, facoltative o nessuna (cap. 320, 13/10/2026);
 *   · un codice a barre entra solo se la cifra di controllo torna: l'errore dice quale delle
 *     due cose non va (lunghezza o cifra), mai un generico «non valido».
 * Bersagli da 40 px: la console si usa anche da tablet.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { SoftChip } from '../SoftChip';
import { UNITA_MISURA_BASE, normalizzaMpn, validaGtin, type UnitaMisuraBase } from '@shared/anagrafe/identificativi';
import type { ComponenteCanonico, ConfezioneAnagrafe, Marchio, RegimeMatricoleConsole } from '../../types/anagrafe';
import type { PatchComponente } from '../../services/anagrafeService';

const ALTEZZA_TOCCO = 40;

const ETICHETTA_UNITA: Record<UnitaMisuraBase, string> = {
  pz: 'pezzi',
  m: 'metri',
  mq: 'metri quadri',
  mc: 'metri cubi',
  kg: 'chilogrammi',
  l: 'litri',
};

/** Lo stato del form, in una forma comoda per l'editing. */
export interface RiconoscimentoValori {
  marchio_id: string | null;
  unita_misura_base: UnitaMisuraBase;
  gtin: string[];
  confezioni: Array<{ nome: string; pezzi: string; gtin: string }>;
  /** 'categoria' = nessuna eccezione: vale il regime della categoria. */
  matricola: 'categoria' | RegimeMatricoleConsole;
  formato_matricola: string;
  classe_etim: string;
}

export function valoriDaComponente(c: ComponenteCanonico): RiconoscimentoValori {
  return {
    marchio_id: c.marchio_id,
    unita_misura_base: c.unita_misura_base,
    gtin: [...c.gtin],
    confezioni: c.confezioni.map((x) => ({ nome: x.nome, pezzi: String(x.pezzi), gtin: x.gtin ?? '' })),
    matricola: c.matricole ?? 'categoria',
    formato_matricola: c.formato_matricola ?? '',
    classe_etim: c.classe_etim ?? '',
  };
}

/** Il patch per il servizio: solo ciò che questa sezione governa. */
export function aPatch(v: RiconoscimentoValori): PatchComponente {
  const confezioni: ConfezioneAnagrafe[] = v.confezioni
    .map((c) => ({ nome: c.nome.trim(), pezzi: Number(c.pezzi), ...(c.gtin.trim() ? { gtin: c.gtin.trim() } : {}) }))
    .filter((c) => c.nome !== '' && Number.isFinite(c.pezzi) && c.pezzi > 0);
  return {
    marchio_id: v.marchio_id,
    unita_misura_base: v.unita_misura_base,
    gtin: v.gtin,
    confezioni,
    matricole: v.matricola === 'categoria' ? undefined : v.matricola,
    formato_matricola: v.formato_matricola.trim() || undefined,
    classe_etim: v.classe_etim.trim() || undefined,
  };
}

interface Props {
  componente: ComponenteCanonico;
  /** Il codice del costruttore come lo si sta battendo nella sezione Identità (per il derivato vivo). */
  codiceCostruttoreCorrente: string;
  marchi: Marchio[];
  valori: RiconoscimentoValori;
  onChange: (v: RiconoscimentoValori) => void;
}

export const RiconoscimentoMagazzino: React.FC<Props> = ({ componente, codiceCostruttoreCorrente, marchi, valori, onChange }) => {
  const [nuovoGtin, setNuovoGtin] = useState('');
  const [erroreGtin, setErroreGtin] = useState<string | null>(null);

  const marchiAttivi = useMemo(() => marchi.filter((m) => m.attivo), [marchi]);
  const marchioScelto = useMemo(() => marchiAttivi.find((m) => m.id === valori.marchio_id) ?? null, [marchiAttivi, valori.marchio_id]);
  const codiceNormalizzato = normalizzaMpn(codiceCostruttoreCorrente);

  const set = (patch: Partial<RiconoscimentoValori>) => onChange({ ...valori, ...patch });

  const aggiungiGtin = () => {
    const esito = validaGtin(nuovoGtin);
    if (!esito.valido) {
      setErroreGtin(
        esito.motivo === 'vuoto'
          ? 'Scrivi il codice a barre, solo cifre.'
          : esito.motivo === 'lunghezza'
            ? `Un codice GS1 ha 8, 12, 13 o 14 cifre: questo ne ha ${esito.gtin.length}.`
            : 'La cifra di controllo non torna: ricontrolla il codice, una cifra è sbagliata.',
      );
      return;
    }
    if (valori.gtin.includes(esito.gtin)) { setErroreGtin('Questo codice è già sul componente.'); return; }
    set({ gtin: [...valori.gtin, esito.gtin] });
    setNuovoGtin('');
    setErroreGtin(null);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Come si riconosce sullo scaffale</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Ciò che il magazzino usa per capire che il pezzo letto è questo componente. Il prezzo non abita qui.
      </Typography>

      {componente.marchio_da_assegnare && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
          Marchio da assegnare: {componente.marchio_nota ?? 'la scrittura trovata non è un marchio'}.
          {componente.costruttore ? ` Era scritto «${componente.costruttore}».` : ''}
        </Alert>
      )}

      <Stack spacing={2}>
        <Autocomplete
          options={marchiAttivi}
          value={marchioScelto}
          onChange={(_, m) => set({ marchio_id: m ? m.id : null })}
          getOptionLabel={(m) => m.nome_canonico}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="marchio"
              size="small"
              helperText={marchioScelto ? undefined : 'Nessun marchio: il componente resta riconoscibile solo dal codice.'}
            />
          )}
          slotProps={{ listbox: { sx: { '& .MuiAutocomplete-option': { minHeight: ALTEZZA_TOCCO } } } }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Tooltip title="Derivato dal codice del costruttore: maiuscolo, senza spazi né separatori. Non si batte.">
            <TextField
              label="codice normalizzato"
              size="small"
              fullWidth
              value={codiceNormalizzato ?? ''}
              placeholder="dal codice del costruttore"
              InputProps={{ readOnly: true, sx: { fontFamily: 'var(--f-mono)', fontSize: 13 } }}
            />
          </Tooltip>
          <TextField
            label="si conta in"
            size="small"
            select
            fullWidth
            value={valori.unita_misura_base}
            onChange={(e) => set({ unita_misura_base: e.target.value as UnitaMisuraBase })}
            helperText={componente.unita_misura_origine === 'dedotta' ? 'dedotta dai prodotti collegati, non ancora confermata' : 'confermata'}
          >
            {UNITA_MISURA_BASE.map((u) => (
              <MenuItem key={u} value={u} sx={{ minHeight: ALTEZZA_TOCCO }}>{ETICHETTA_UNITA[u]} ({u})</MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* codici a barre */}
        <Box>
          <Typography variant="body2" sx={{ mb: 0.75 }}>Codici a barre</Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mb: valori.gtin.length ? 1 : 0 }}>
            {valori.gtin.map((g) => (
              <Stack key={g} direction="row" alignItems="center" spacing={0.25}>
                <SoftChip label={g} tone="info" mono />
                <IconButton size="small" aria-label={`Togli il codice a barre ${g}`} onClick={() => set({ gtin: valori.gtin.filter((x) => x !== g) })} sx={{ width: 32, height: 32 }}>
                  <DeleteOutline sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              placeholder="aggiungi un codice a barre (EAN-13, UPC, GTIN-14)"
              value={nuovoGtin}
              onChange={(e) => { setNuovoGtin(e.target.value); if (erroreGtin) setErroreGtin(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aggiungiGtin(); } }}
              error={!!erroreGtin}
              helperText={erroreGtin ?? 'Solo se stampato sull’etichetta o sulla confezione: nel fotovoltaico spesso non c’è.'}
              inputProps={{ inputMode: 'numeric', style: { fontFamily: 'var(--f-mono)', fontSize: 13 } }}
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" onClick={aggiungiGtin} sx={{ minHeight: ALTEZZA_TOCCO, whiteSpace: 'nowrap' }}>Aggiungi</Button>
          </Stack>
        </Box>

        {/* confezioni */}
        <Box>
          <Typography variant="body2" sx={{ mb: 0.75 }}>Confezioni</Typography>
          <Stack spacing={1}>
            {valori.confezioni.map((c, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="bancale, cartone…"
                  value={c.nome}
                  onChange={(e) => set({ confezioni: valori.confezioni.map((x, j) => (j === i ? { ...x, nome: e.target.value } : x)) })}
                  sx={{ flex: 2 }}
                />
                <TextField
                  size="small"
                  placeholder="pezzi"
                  value={c.pezzi}
                  onChange={(e) => set({ confezioni: valori.confezioni.map((x, j) => (j === i ? { ...x, pezzi: e.target.value.replace(/[^\d]/g, '') } : x)) })}
                  inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                  sx={{ width: 96 }}
                />
                <TextField
                  size="small"
                  placeholder="codice a barre della confezione"
                  value={c.gtin}
                  onChange={(e) => set({ confezioni: valori.confezioni.map((x, j) => (j === i ? { ...x, gtin: e.target.value } : x)) })}
                  inputProps={{ inputMode: 'numeric', style: { fontFamily: 'var(--f-mono)', fontSize: 13 } }}
                  sx={{ flex: 2 }}
                />
                <IconButton aria-label="Togli la confezione" onClick={() => set({ confezioni: valori.confezioni.filter((_, j) => j !== i) })} sx={{ width: ALTEZZA_TOCCO, height: ALTEZZA_TOCCO }}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              startIcon={<AddCircleOutline />}
              size="small"
              onClick={() => set({ confezioni: [...valori.confezioni, { nome: '', pezzi: '', gtin: '' }] })}
              sx={{ alignSelf: 'flex-start', minHeight: ALTEZZA_TOCCO }}
            >
              Aggiungi confezione
            </Button>
          </Stack>
        </Box>

        {/* matricola per pezzo */}
        <Box>
          <Typography variant="body2" sx={{ mb: 0.75 }}>Le matricole dei pezzi, in magazzino</Typography>
          <ToggleButtonGroup
            exclusive
            value={valori.matricola}
            onChange={(_, v: RiconoscimentoValori['matricola'] | null) => { if (v) set({ matricola: v }); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { minHeight: ALTEZZA_TOCCO, px: 2, textTransform: 'none' } }}
          >
            <ToggleButton value="categoria">come la categoria</ToggleButton>
            <ToggleButton value="obbligatorie">obbligatorie</ToggleButton>
            <ToggleButton value="facoltative">facoltative</ToggleButton>
            <ToggleButton value="assenti">nessuna</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Di norma lo decide la categoria ({componente.categoria_code || 'senza categoria'}); qui si fa solo l'eccezione per questo modello.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="forma della matricola"
            size="small"
            fullWidth
            value={valori.formato_matricola}
            onChange={(e) => set({ formato_matricola: e.target.value })}
            placeholder={marchioScelto?.formato_matricola ?? 'es. ^[A-Z0-9]{24}$'}
            helperText={marchioScelto?.formato_matricola && !valori.formato_matricola ? 'vuoto: vale la forma del marchio' : 'espressione regolare, serve solo a scartare una lettura sbagliata'}
            inputProps={{ style: { fontFamily: 'var(--f-mono)', fontSize: 13 } }}
          />
          <TextField
            label="classe ETIM"
            size="small"
            fullWidth
            value={valori.classe_etim}
            onChange={(e) => set({ classe_etim: e.target.value })}
            placeholder="es. EC001746"
            helperText="facoltativa: il vocabolario standard delle specifiche"
            inputProps={{ style: { fontFamily: 'var(--f-mono)', fontSize: 13 } }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default RiconoscimentoMagazzino;
